import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {DialogComponent} from "../Dialog/Dialog.Component";
import TablesComponent from "../Tables/Tables.Component";
import {GetBulkActionDetails} from "../../Services/SetupsJobRequisition.Services";
import {showError} from "../../Helpers";
import "./OperationsTracker.Style.scss";

const DETAILS_COLUMNS_CONFIG = [
  {key: "status", label: "Shared:status"},
  {key: "position_code", label: "Shared:position-code"},
  {key: "message", label: "Shared:message"},
];

export const FailedItemsDialog = ({
  isOpen,
  bulkOperationUuid,
  onClose,
  parentTranslationPath,
}) => {
  const {t} = useTranslation([parentTranslationPath, "Shared"]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({limit: 10, page: 1});
  const [details, setDetails] = useState(() => ({
    results: [],
    totalCount: 0,
  }));

  const getBulkActionDetails = useCallback(async () => {
    setIsLoading(true);
    const response = await GetBulkActionDetails({
      ...filter,
      bulk_operation_uuid: bulkOperationUuid,
    });
    setIsLoading(false);
    if (response && response.status === 200)
      setDetails({
        results: response.data?.results ?? [],
        totalCount: response.data?.paginate?.total ?? 0,
      });
    else {
      setDetails({results: [], totalCount: 0});
      showError(t("Shared:failed-to-get-saved-data"), response);
    }
  }, [bulkOperationUuid, filter, t]);

  useEffect(() => {
    if (isOpen && bulkOperationUuid) getBulkActionDetails();
  }, [isOpen, bulkOperationUuid, getBulkActionDetails]);

  const onPageIndexChanged = useCallback((newIndex) => {
    setFilter((items) => ({...items, page: newIndex + 1}));
  }, []);

  const onPageSizeChanged = useCallback((newPageSize) => {
    setFilter((items) => ({...items, page: 1, limit: newPageSize}));
  }, []);

  const headerData = useMemo(
    () =>
      DETAILS_COLUMNS_CONFIG.map((column) => ({
        isHidden: false,
        input: column.key,
        label: t(column.label),
        component: (row) =>
          column.key === "status" ? (
            <span className="operation-details-status">{row.status}</span>
          ) : (
            <span>{row[column.key] || "--"}</span>
          ),
      })),
    [t]
  );

  return (
    <DialogComponent
      titleText={"Shared:operation-details"}
      maxWidth="md"
      isOpen={isOpen}
      onCloseClicked={onClose}
      dialogContent={
        <div className="operation-details-dialog-wrapper">
          <TablesComponent
            isWithoutBoxWrapper
            isWithEmpty
            data={details.results}
            headerData={headerData}
            pageIndex={filter.page - 1}
            pageSize={filter.limit}
            totalItems={details.totalCount}
            isLoading={isLoading}
            uniqueKeyInput="uuid"
            onPageIndexChanged={onPageIndexChanged}
            onPageSizeChanged={onPageSizeChanged}
            parentTranslationPath={parentTranslationPath}
          />
        </div>
      }
      parentTranslationPath={parentTranslationPath}
    />
  );
};

FailedItemsDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  bulkOperationUuid: PropTypes.string,
  onClose: PropTypes.func,
  parentTranslationPath: PropTypes.string,
};
